import { Box, SxProps } from '@mui/system'
import React, { useEffect, useCallback } from 'react'
import { makeStyles } from 'tss-react/mui'
import StickyContainer from './StickyContainer'
import { Card } from '@mui/material'
import { useIsElementSticky } from 'src/Libs/navigation'
import { useStickyBar, useStickyBarHeight } from '@e3dc-react/shell/Libs/Header'

interface StyleProps {
  isSticky?: boolean
}

export const useStickyStyles = makeStyles<StyleProps>()((theme, props) => ({
  card: {
    padding: props.isSticky ? theme.spacing(2) : theme.spacing(2, 2),
    borderRadius: props.isSticky ? 0 : theme.shape.borderRadius,
    marginLeft: props.isSticky ? theme.spacing(-2) : 0,
    marginRight: props.isSticky ? theme.spacing(-2) : 0,
    boxShadow: props.isSticky ? '0px 4px 12px -4px rgba(0, 0, 0, 0.1)' : theme.effectStyles.shadowMain,
    borderTop: props.isSticky ? `1px solid ${theme.palette.background.paperBorder}` : 'none',

    [theme.breakpoints.up('md')]: {
      marginTop: -theme.spacing(1.5),
      marginLeft: 0,
      marginRight: 0,
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
  },
}))

interface StickyContentBarProps {
  children: React.ReactNode
  sx?: SxProps
}

/**
 * Shared component to wrap content which should be sticky to the header when scrolling
 */
const StickyContentBar: React.FC<StickyContentBarProps> = ({ children, sx }) => {
  const { isSticky, stickyRef } = useIsElementSticky()
  const { setHasStickyBar } = useStickyBar()
  const { setStickyBarHeight } = useStickyBarHeight()
  const { classes } = useStickyStyles({ isSticky })

  useEffect(() => {
    setHasStickyBar(isSticky)
  }, [isSticky, setHasStickyBar])

  const measuredRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node !== null) {
        const height = node.getBoundingClientRect().height
        if (height > 0) setStickyBarHeight(height)
      }
    },
    [setStickyBarHeight]
  )

  return (
    <>
      <Box ref={stickyRef} />
      <StickyContainer sxStyle={{ ...sx, mt: isSticky ? 0 : -2 }} isSticky={isSticky}>
        <Card
          ref={measuredRef}
          className={classes.card}
          sx={theme => ({
            [theme.breakpoints.down('sm')]: { transition: theme.transitions.create('all') },
          })}
        >
          {children}
        </Card>
      </StickyContainer>
    </>
  )
}

export default StickyContentBar
